import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  Grid,
  IconButton,
  Divider,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { leadApi } from '../services/api';
import { useApp } from '../context/AppContext';
import { LoadingSpinner } from '../components/common';
import { nicheConfig } from '../theme';

const niches = Object.keys(nicheConfig);
const stages = [
  'new', 'warming', 'dm_sent', 'replied', 'call_scheduled',
  'call_completed', 'proposal_sent', 'negotiating', 'won', 'lost', 'not_interested'
];
const sources = [
  'hashtag_search', 'referral', 'facebook_group', 'instagram_ad', 'website', 'other'
];

const LeadForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSnackbar, fetchDashboardData } = useApp();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    instagramHandle: '',
    businessName: '',
    ownerName: '',
    email: '',
    phone: '',
    niche: '',
    city: '',
    stateProvince: '',
    country: 'US',
    followerCount: '',
    postCount: '',
    hasWebsite: false,
    currentBookingMethod: '',
    stage: 'new',
    nextFollowupDate: null,
    followupNotes: '',
    source: '',
    notes: '',
  });

  useEffect(() => {
    if (isEdit) {
      fetchLead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const fetchLead = async () => {
    try {
      const response = await leadApi.getById(id);
      const lead = response.data;
      setFormData({
        instagramHandle: lead.instagramHandle || '',
        businessName: lead.businessName || '',
        ownerName: lead.ownerName || '',
        email: lead.email || '',
        phone: lead.phone || '',
        niche: lead.niche || '',
        city: lead.city || '',
        stateProvince: lead.stateProvince || '',
        country: lead.country || 'US',
        followerCount: lead.followerCount || '',
        postCount: lead.postCount || '',
        hasWebsite: lead.hasWebsite || false,
        currentBookingMethod: lead.currentBookingMethod || '',
        stage: lead.stage || 'new',
        nextFollowupDate: lead.nextFollowupDate ? new Date(lead.nextFollowupDate) : null,
        followupNotes: lead.followupNotes || '',
        source: lead.source || '',
        notes: lead.notes || '',
      });
    } catch (error) {
      showSnackbar('Failed to load lead', 'error');
      navigate('/leads');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.instagramHandle || !formData.niche) {
      showSnackbar('Instagram handle and niche are required', 'error');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        followerCount: formData.followerCount ? parseInt(formData.followerCount) : 0,
        postCount: formData.postCount ? parseInt(formData.postCount) : 0,
      };

      if (isEdit) {
        await leadApi.update(id, data);
        showSnackbar('Lead updated successfully', 'success');
      } else {
        await leadApi.create(data);
        showSnackbar('Lead created successfully', 'success');
      }
      fetchDashboardData();
      navigate('/leads');
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to save lead', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading lead..." />;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(-1)}>
          <BackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={700}>
          {isEdit ? 'Edit Lead' : 'Add New Lead'}
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Basic Info */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Basic Information
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Instagram Handle"
                  name="instagramHandle"
                  value={formData.instagramHandle}
                  onChange={handleChange}
                  required
                  placeholder="@username"
                  disabled={isEdit}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Business Name"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Owner Name"
                  name="ownerName"
                  value={formData.ownerName}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Niche"
                  name="niche"
                  value={formData.niche}
                  onChange={handleChange}
                  required
                >
                  {niches.map((niche) => (
                    <MenuItem key={niche} value={niche}>
                      {nicheConfig[niche].emoji} {nicheConfig[niche].label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Contact Info */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Contact Information
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </Grid>

              {/* Location */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Location
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="State/Province"
                  name="stateProvince"
                  value={formData.stateProvince}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                />
              </Grid>

              {/* Instagram Metrics */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Instagram Metrics
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Follower Count"
                  name="followerCount"
                  type="number"
                  value={formData.followerCount}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Post Count"
                  name="postCount"
                  type="number"
                  value={formData.postCount}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Current Booking Method"
                  name="currentBookingMethod"
                  value={formData.currentBookingMethod}
                  onChange={handleChange}
                  placeholder="e.g., DM, Linktree, Calendly"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.hasWebsite}
                      onChange={handleChange}
                      name="hasWebsite"
                    />
                  }
                  label="Has existing website"
                />
              </Grid>

              {/* Pipeline */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Pipeline Status
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Stage"
                  name="stage"
                  value={formData.stage}
                  onChange={handleChange}
                >
                  {stages.map((stage) => (
                    <MenuItem key={stage} value={stage}>
                      {stage.replace('_', ' ').toUpperCase()}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Source"
                  name="source"
                  value={formData.source}
                  onChange={handleChange}
                >
                  <MenuItem value="">None</MenuItem>
                  {sources.map((source) => (
                    <MenuItem key={source} value={source}>
                      {source.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Follow-up */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Follow-up
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Next Follow-up Date"
                  value={formData.nextFollowupDate}
                  onChange={(date) => setFormData({ ...formData, nextFollowupDate: date })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Follow-up Notes"
                  name="followupNotes"
                  value={formData.followupNotes}
                  onChange={handleChange}
                />
              </Grid>

              {/* Notes */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Notes
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  multiline
                  rows={4}
                />
              </Grid>

              {/* Actions */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : isEdit ? 'Update Lead' : 'Create Lead'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LeadForm;
